import { ExpandedContent, Separator, EmptyState } from "../../Compartilhado/styles";
import { IItemHistoricoServico } from "../../../../types";
import BlocoAlteracoes from "../../Compartilhado/Blocos/BlocoAlteracoes";
import { CampoAlteracao } from "../../Compartilhado/Blocos/BlocoAlteracoes";
import { formatServicoColuna } from "../../../../../../../utils/servicoMapperPlanos";

interface ExpandedContentProps {
  item: IItemHistoricoServico;
  isExpanded?: boolean;
}

function formatarValor(valor: any, isMonetary: boolean = false): string {
  if (valor === null || valor === undefined) return '—';
  if (typeof valor === 'boolean') return valor ? 'Sim' : 'Não';
  if (typeof valor === 'number') {
    // Se for um valor monetário, sempre formatar como R$
    if (isMonetary) {
      return `R$ ${valor.toFixed(2).replace('.', ',')}`;
    }
    // Se for um número maior que 1 e tem decimais, também formatar como moeda
    if (valor > 1 && valor % 1 !== 0) {
      return `R$ ${valor.toFixed(2).replace('.', ',')}`;
    }
    return String(valor);
  }
  return String(valor);
}

function mapearDadosServicoEditado(payload: any): CampoAlteracao[] {
  if (!payload || !payload.antes || !payload.depois) return [];

  const campos: CampoAlteracao[] = [];
  const antes = payload.antes;
  const depois = payload.depois;

  if (antes.servico_ativo !== depois.servico_ativo) {
    campos.push({
      campo: 'Serviço Ativo',
      antes: formatarValor(antes.servico_ativo),
      depois: formatarValor(depois.servico_ativo)
    });
  }
  // Verificar mudança de modo de precificação
  const mudouParaSobConsulta = antes.modo_precificacao !== 'sob_consulta' && depois.modo_precificacao === 'sob_consulta';
  const mudouDeSobConsulta = antes.modo_precificacao === 'sob_consulta' && depois.modo_precificacao !== 'sob_consulta';

  if (antes.modo_precificacao !== depois.modo_precificacao) {
    campos.push({
      campo: 'Modo de Precificação',
      antes: antes.modo_precificacao === 'sob_consulta' ? 'Sob Consulta' : 'Fixo em Créditos',
      depois: depois.modo_precificacao === 'sob_consulta' ? 'Sob Consulta' : 'Fixo em Créditos'
    });

    // Quando muda para "sob consulta", mostrar que créditos foram removidos e modalidade/profissional com X
    if (mudouParaSobConsulta) {
      campos.push({
        campo: 'Créditos Padrão',
        antes: formatarValor(antes.creditos_padrao, false),
        depois: '—'
      });
      if (antes.modalidade) {
        campos.push({ campo: 'Modalidade', antes: formatarValor(antes.modalidade), depois: '—' });
      }
      if (antes.profissional) {
        campos.push({ campo: 'Profissional', antes: formatarValor(antes.profissional), depois: '—' });
      }
    }
    // Quando sai de "sob consulta", mostrar os novos valores e modalidade/profissional se existir
    if (mudouDeSobConsulta) {
      campos.push({
        campo: 'Créditos Padrão',
        antes: '—',
        depois: formatarValor(depois.creditos_padrao, false)
      });
      if (depois.modalidade) {
        campos.push({ campo: 'Modalidade', antes: '—', depois: formatarValor(depois.modalidade) });
      }
      if (depois.profissional) {
        campos.push({ campo: 'Profissional', antes: '—', depois: formatarValor(depois.profissional) });
      }
    }
  }

  // Verificar mudanças em créditos apenas se não mudou o modo de precificação
  if (!mudouParaSobConsulta && !mudouDeSobConsulta) {
    if (antes.creditos_padrao !== depois.creditos_padrao) {
      campos.push({
        campo: 'Créditos Padrão',
        antes: formatarValor(antes.creditos_padrao, false),
        depois: formatarValor(depois.creditos_padrao, false)
      });
    }
  }
  if (antes.descricao !== depois.descricao) {
    campos.push({
      campo: 'Descrição',
      antes: formatarValor(antes.descricao),
      depois: formatarValor(depois.descricao)
    });
  }

  return campos;
}

export default function ExpandedContentServicoEditado({ item, isExpanded }: ExpandedContentProps) {
  const payload = typeof item.payload === 'string' ? JSON.parse(item.payload || '{}') : (item.payload || {});

  // Formato agrupado: múltiplos serviços em um único log (igual ao histórico de planos)
  if (payload?.servicos && Array.isArray(payload.servicos) && payload.servicos.length > 0) {
    const blocos = payload.servicos
      .map((s: any, idx: number) => {
        const campos = mapearDadosServicoEditado({ antes: s.antes, depois: s.depois });
        if (campos.length === 0) return null;
        const nomeServico = s.nome_servico || formatServicoColuna(null, s.servico, s.tipo_audiencia) || 'Serviço';
        return (
          <BlocoAlteracoes
            key={s.id_servico_template ?? idx}
            campos={campos}
            sectionTitle={nomeServico}
            tituloComoFaixa
          />
        );
      })
      .filter(Boolean);

    if (blocos.length === 0) {
      return (
        <ExpandedContent>
          <Separator />
          <EmptyState>Nenhuma alteração detectada.</EmptyState>
        </ExpandedContent>
      );
    }

    return (
      <ExpandedContent>
        <Separator />
        <h4 className="section-title">PRECIFICAÇÃO DE SERVIÇOS</h4>
        {blocos}
      </ExpandedContent>
    );
  }

  // Formato único serviço
  const campos = mapearDadosServicoEditado(payload);

  if (campos.length === 0) {
    return (
      <ExpandedContent>
        <Separator />
        <EmptyState>Nenhuma alteração detectada.</EmptyState>
      </ExpandedContent>
    );
  }

  return (
    <ExpandedContent>
      <Separator />
      <BlocoAlteracoes
        campos={campos}
        sectionTitle="PRECIFICAÇÃO DE SERVIÇOS"
        tituloComoFaixa
      />
    </ExpandedContent>
  );
}
